<script lang="ts">
    import { KEY_ENTER } from "@/constants";
    import clickOutside from "@ui-utils/actions/clickOutside";
    import CN from "classnames";
    import { createEventDispatcher } from "svelte";

    import Icon from "@components/Icon.svelte";
    import ChevronIcon from "@components/icons/ChevronIcon.svelte";

    import SelectList from "./SelectList.svelte";
    import type { ItemType } from "./types";

    export let selectClass = "";
    export let selectedClass = "";
    export let hasError = false;
    export let isDisabled = false;
    export let isFocused = false;
    export let searchable = false;
    export let listOpen = false;
    export let placeholderText = "";
    export let items: ItemType[];
    export let value: string | number;
    export let searchValue = "";
    export let inputAttributes: { class?: string; name?: string } = {
        class: "",
        name: "",
    };

    $: selected = items.find((item) => item.value === value) || {
        value,
        label: "",
    };

    $: if (searchable && selected.label) {
        searchValue = selected.label;
    }

    const dispatch = createEventDispatcher();
    const ariaValues = (values: string) => {
        return `Option ${values}, selected.`;
    };

    const toggleList = () => {
        listOpen = !listOpen;
    };

    const handleFocus = () => {
        isFocused = true;
    };

    const handleClick = () => {
        if (isDisabled) return;
        isFocused = true;
        toggleList();
    };

    const handleClose = () => {
        isFocused = false;
        listOpen = false;
    };

    const handleSelect = (item: ItemType) => {
        searchValue = item.label;
        dispatch("change", { value: item.value });
    };

    const handleKeydown = (e: KeyboardEvent) => {
        if (e.key === KEY_ENTER && !listOpen) {
            toggleList();
        }
    };

    $: ariaSelection = ariaValues(selected.label);
    $: ariaContext = "";

    const handleInputChange = (event: any) => {
        searchValue = event.target.value;
    };
</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<div
    class={CN("gw-select", selectClass, {
        "gw-select--error": hasError,
        "gw-select--disabled": isDisabled,
        "gw-select--focused": isFocused,
        "gw-select--opened": listOpen,
    })}
    use:clickOutside
    on:clickOutside={handleClose}
    on:click={handleClick}
    aria-haspopup="listbox"
    aria-expanded={listOpen}
    aria-labelledby="selected-option-label"
>
    <span
        aria-live="polite"
        aria-atomic="false"
        aria-relevant="additions text"
        class="gw-hide-elements"
    >
        {#if isFocused}
            <span id="aria-selection">{ariaSelection}</span>
            <span id="aria-context">
                {ariaContext}
            </span>
        {/if}
    </span>

    <input
        name="select-input"
        readOnly={!searchable}
        on:focus={handleFocus}
        on:keydown={handleKeydown}
        on:input={handleInputChange}
        placeholder={placeholderText}
        disabled={isDisabled}
        value={searchable ? searchValue : selected.label || ""}
        tabindex="0"
        type="text"
        class="gw-select__input gw-focus-reset hello-nazar"
    />

    <div class={CN("gw-select__selected", selectedClass)}>
        <div class="gw-select__selection svelte-pu1q1n">
            <slot name="label" />
        </div>
    </div>

    <SelectList
        {items}
        {searchValue}
        {searchable}
        selectedOption={value}
        on:select={(event) => handleSelect(event.detail)}
        {listOpen}
    />

    <div
        class={CN("gw-select__icon", {
            "gw-select__icon-open": listOpen,
        })}
    >
        <Icon width={12} height={12} color="#3f4f5f">
            <ChevronIcon />
        </Icon>
    </div>

    <input type="hidden" name={inputAttributes.name} value={selected.value} />
</div>

<style lang="postcss">
    .gw-select {
        --input-border-color: rgba(38, 40, 66, 0.12);
        --input-border-color-focus: #007995;
        --input-border-radius: 10px;

        display: flex;
        position: relative;
        box-sizing: border-box;
        align-items: center;
        padding: 0.75em;
        border: 1px solid var(--input-border-color);
        border-radius: var(--input-border-radius);
        background: #fff;
        line-height: 1;

        &__icon {
            opacity: 0.5;
            transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
        }
        &__icon-open {
            transform: rotate(180deg);
        }

        &__input {
            width: 100%;
            border: none;
            padding: 0;
            background: transparent;
            color: var(--inputColor, #3f4f5f);
            cursor: pointer;

            &::placeholder {
                opacity: var(--placeholderOpacity, 1);
                color: var(--placeholderColor, #78848f);
            }

            &:focus {
                outline: none;
            }
        }

        &__selected {
            padding: var(--selectedItemPadding, 0 20px 0 0);
            overflow-x: hidden;
            line-height: var(--height, 42px);

            &:focus {
                outline: none;
            }
        }
    }
</style>
